import { Visibility, VisibilityOff } from '@mui/icons-material';
import { TextField, Button, Typography, Stack, Grid, Divider, InputAdornment, IconButton, Link } from '@mui/material';
import { useState } from 'react';
import useUserData from '../../hooks/useUserData';
import { UserInfo } from "../../hooks/useUserInfo";


interface Props {
  onClose: () => void;
  userInfo: UserInfo | undefined;
}


export default function ApiKeyManagement({ userInfo }: Props) {
  const { apiKey, setApiKey } = useUserData();
  const [hasUpdatedApiKey, setHasUpdatedApiKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  function handleChangeApiKey(newKey: string | undefined) {
    setApiKey(newKey);
    setHasUpdatedApiKey(true);
    setTimeout(() => setHasUpdatedApiKey(false), 2000); // Reset after 2 seconds
  }

  function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
    const pasteData = event.clipboardData.getData('text');
    handleChangeApiKey(pasteData);
    setShowApiKey(true);
    setTimeout(() => setShowApiKey(false), 500); // Reset after 1/2 second
    event.preventDefault(); // Prevent the default paste action
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // Allow Ctrl+V and Command+V (paste)
    // Allow Ctrl+C and Command+C (copy)
    // Allow Ctrl+A and Command+A (select all)
    if ((event.ctrlKey || event.metaKey) &&
	(event.key === 'v' || event.key === 'a' || event.key === 'c')) {
      return;
    }
    // Allow arrow keys
    if (event.key.match(/^Arrow/)) {
      return;
    }
    
    event.preventDefault(); // Prevent all key inputs
  }
  
  return <>
    <Grid container spacing={1}>
      <Typography>
	You can provide your own OpenAI API key to use <strong>unprompted</strong> at your own expense.
	The key will be saved in plaintext only in your browser's local storage. A secure, one-way encrypted version is stored on our servers for usage accounting purposes.

      </Typography>
      <Typography>
	<Link
	  href="https://platform.openai.com/api-keys"
	  rel="noopener"
	  target="_blank"
	>
	  Get your key here.
	</Link>
      </Typography>

    </Grid>

    <Divider sx={{ my: 2 }} /> {/* This adds top and bottom margins */}

    <Stack spacing={2}>
      <Typography variant="h6" component="h3">
	OpenAI API key for user: <strong><code>{userInfo?.username}</code></strong> a.k.a. <strong>{userInfo?.nickname}</strong>
      </Typography>
      
      <TextField
        label="Paste in your OpenAI API key here"
        fullWidth
        value={showApiKey ? (apiKey ?? "") : (apiKey ? "<API key hidden by default for your security>" : "")}
        onChange={(e) => handleChangeApiKey(e.target.value)}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowApiKey(!showApiKey)}
                onMouseDown={(e) => e.preventDefault()}
              >
			{showApiKey ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Typography style={{
	visibility: hasUpdatedApiKey ? 'visible' : 'hidden',
      }}>
	Your API key has been {apiKey ? 'updated' : 'removed'}.
      </Typography>
      <Button
	disabled={!apiKey}
	onClick={() => handleChangeApiKey(undefined)}
      >
	Remove API Key
      </Button>
      
    </Stack>
  </>;
}
